import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Calendar, Check, ChevronDown } from 'lucide-react';
import { RANGES, RangeOption } from '../../data/onboardingFunnel';
interface DateRangePickerProps {
  value: RangeOption;
  onChange: (value: RangeOption) => void;
}

/** Reporting period selector: preset windows plus an explicit custom range. */
export function DateRangePicker({
  value,
  onChange
}: DateRangePickerProps) {
  const [open, setOpen] = useState(false);
  const [custom, setCustom] = useState(false);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const applyCustom = () => {
    if (!from || !to) return;
    const days = Math.max(1, Math.round((new Date(to).getTime() - new Date(from).getTime()) / 86_400_000));
    onChange({
      label: `${from} → ${to}`,
      days,
      comparison: 'vs preceding period'
    });
    setOpen(false);
    setCustom(false);
  };
  return <div className="relative inline-block" data-id="element-1154" data-mp="1szbw6cfgz7ze">
      <button type="button" onClick={() => setOpen(o => !o)} aria-haspopup="listbox" aria-expanded={open} className="flex items-center gap-2 rounded-xl border border-neutral-200 bg-white px-3 py-2 text-[13px] font-semibold text-[#2B211A] transition-colors hover:border-neutral-300" data-id="element-1155" data-mp="gt4fiog1lzg1">
        <Calendar size={14} className="text-neutral-400" data-id="element-1156" data-mp="bu1av53z5v8l" />
        {value.label}
        <ChevronDown size={14} className={`text-neutral-400 transition-transform ${open ? 'rotate-180' : ''}`} data-id="element-1157" data-mp="bw5o4dnrk6lj" />
      </button>

      <AnimatePresence data-id="element-1158" data-mp="2u4t7ng05wn1">
        {open && <>
            {/* Click-away layer */}
            <button type="button" aria-label="Close period selector" onClick={() => {
          setOpen(false);
          setCustom(false);
        }} className="fixed inset-0 z-10 cursor-default" data-id="element-1159" data-mp="3kae7qw8b5n1k" />

            <motion.div initial={{
          opacity: 0,
          y: -4
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -4
        }} transition={{
          duration: 0.14
        }} className="absolute left-0 z-20 mt-1.5 w-60 overflow-hidden rounded-xl border border-neutral-200 bg-white shadow-lg" data-id="element-1160" data-mp="3b9ohs95qaqvr">
              <ul role="listbox" aria-label="Reporting period" className="py-1" data-id="element-1161" data-mp="1gzho260yni8y">
                {RANGES.map(preset => <li key={preset.label} role="option" aria-selected={value.label === preset.label} data-id="element-1162" data-mp="1c0eov27nw68w">
                    <button type="button" onClick={() => {
                onChange(preset);
                setOpen(false);
                setCustom(false);
              }} className="flex w-full items-center gap-2 px-3.5 py-2 text-left text-[13.5px] transition-colors hover:bg-neutral-50" data-id="element-1163" data-mp="17z2ygugyrgzn">
                      <span className={`flex-1 ${value.label === preset.label ? 'font-bold' : 'font-medium'}`} data-id="element-1164" data-mp="1vsl2w5n63hvz">
                        {preset.label}
                      </span>
                      {value.label === preset.label && <Check size={14} className="text-[#EA580C]" data-id="element-1165" data-mp="fc8gc47mctpc" />}
                    </button>
                  </li>)}
              </ul>

              <div className="border-t border-neutral-100" data-id="element-1166" data-mp="2r0n3ji1cp09b">
                {!custom ? <button type="button" onClick={() => setCustom(true)} className="w-full px-3.5 py-2.5 text-left text-[13.5px] font-medium transition-colors hover:bg-neutral-50" data-id="element-1167" data-mp="2vyf4t2nvxyq6">
                    Custom range…
                  </button> : <div className="p-3.5" data-id="element-1168" data-mp="34s0l4qfp7o1p">
                    <label className="block text-[11px] font-bold uppercase tracking-wider text-neutral-400" data-id="element-1169" data-mp="34t3cezgxnsi5">
                      From
                      <input type="date" value={from} onChange={e => setFrom(e.target.value)} className="mt-1 w-full rounded-lg border border-neutral-200 px-2.5 py-1.5 text-[13px] font-normal normal-case tracking-normal text-[#2B211A] outline-none focus:border-[#EA580C]" data-id="element-1170" data-mp="3eon3n8kjy30k" />
                    </label>
                    <label className="mt-2.5 block text-[11px] font-bold uppercase tracking-wider text-neutral-400" data-id="element-1171" data-mp="1paqbxn40sye1">
                      To
                      <input type="date" value={to} onChange={e => setTo(e.target.value)} className="mt-1 w-full rounded-lg border border-neutral-200 px-2.5 py-1.5 text-[13px] font-normal normal-case tracking-normal text-[#2B211A] outline-none focus:border-[#EA580C]" data-id="element-1172" data-mp="1tcbu9nzbq4vm" />
                    </label>
                    <button type="button" onClick={applyCustom} disabled={!from || !to} className={`mt-3 w-full rounded-lg px-3 py-2 text-[13px] font-bold transition-colors ${from && to ? 'bg-[#EA580C] text-white hover:bg-[#d24e08]' : 'cursor-not-allowed bg-neutral-100 text-neutral-400'}`} data-id="element-1173" data-mp="17zb56wps49w2">
                      Apply
                    </button>
                  </div>}
              </div>
            </motion.div>
          </>}
      </AnimatePresence>
    </div>;
}